<template>
  <figure class="gallery-film" :class="{'is-dark':isDark,'is-unframed':unframed}">
    <div class="gallery-film-frame">
      <video ref="video" class="gallery-film-video" :src="started ? source : undefined" :poster="poster" :controls="started && controlsVisible" :aria-hidden="!started" :tabindex="started ? 0 : -1" :preload="started ? 'metadata' : 'none'" playsinline aria-label="The View Gallery introduction" @play="onPlay" @pause="onPause" @ended="onEnded" @loadedmetadata="restorePlayback" @error="onError" />
      <button v-if="!started" ref="playButton" class="gallery-film-play" type="button" aria-label="Play The View Gallery introduction, 1 minute 42 seconds" @click="play">
        <span class="gallery-film-poster" aria-hidden="true">
          <img class="gallery-film-poster-model" :src="poster" alt="" draggable="false" />
          <span class="gallery-film-poster-heading"><span class="gallery-film-poster-title"><GalleryMark />The View Gallery</span><span class="gallery-film-poster-project">Part of the project <span>AI × Self</span></span></span>
          <span class="gallery-film-poster-url">henryw.me</span>
          <span class="gallery-film-poster-credit">Music · Solace by Scott Buckley · CC BY 4.0</span>
        </span>
        <span class="gallery-film-play-label">
        <span class="gallery-film-play-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 9 6-9 6Z"/></svg></span>
        <span>Watch video<small>1 min 42 sec</small></span>
        </span>
      </button>
    </div>
    <figcaption v-if="failed" class="gallery-film-footer" role="status">
      The video could not play. <a :href="source" target="_blank" rel="noopener">Open the video</a>
    </figcaption>
  </figure>
</template>

<script>
import { initialExhibitionDarkMode } from '../../utils/exhibitionTheme';
import GalleryMark from '../view/GalleryMark.vue';
import { watchVideoControls } from '../../../public/slidepoise/docs/site/video-controls.mjs';
import '../../../public/slidepoise/docs/site/video-controls.css';
const mediaBase = '/assets/video/view-gallery/';

export default {
  name: 'GalleryFilm',
  components: { GalleryMark },
  props: { active: { type: Boolean, default: true }, dark: { type: Boolean, default: null }, unframed: { type: Boolean, default: false } },
  data: () => ({ siteDark: initialExhibitionDarkMode(), started: false, failed: false, controlsVisible: true }),
  computed: {
    isDark() { return this.dark ?? this.siteDark; },
    source() { return `${mediaBase}the-view-gallery${this.isDark ? '-dark' : ''}.mp4?v=20261015-drama3`; },
    poster() { return `${mediaBase}poster${this.isDark ? '-dark' : ''}.webp`; },
  },
  watch: {
    active(value) { if (!value) this.pause(); },
    async source() {
      if (!this.started) return;
      const video = this.$refs.video;
      this._resumeState ??= { time: video.currentTime, playing: !video.paused };
      video.pause();
      this.failed = false;
      await this.$nextTick();
      video.load();
      if (this._resumeState?.playing && this.active && !document.hidden) {
        try { await video.play(); }
        catch (error) { if (error.name !== 'AbortError') this.failed = true; }
      }
    },
  },
  mounted() {
    this._controlsCleanup = watchVideoControls(this.$refs.video, visible => { this.controlsVisible = visible; });
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('view-summary-play', this.otherAudio);
    window.addEventListener('theme-change', this.syncTheme);
    this._observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !document.fullscreenElement && !this.$refs.video?.webkitDisplayingFullscreen) this.pause();
    });
    this._observer.observe(this.$el);
  },
  beforeUnmount() {
    this._controlsCleanup?.();
    this.pause();
    this._observer?.disconnect();
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('view-summary-play', this.otherAudio);
    window.removeEventListener('theme-change', this.syncTheme);
  },
  methods: {
    async play() {
      this.started = true;
      this.failed = false;
      await this.$nextTick();
      if (!this.active) return;
      const video = this.$refs.video;
      video.focus({ preventScroll: true });
      try { await video.play(); }
      catch (error) { if (error.name !== 'AbortError') this.failed = true; }
    },
    onPlay() {
      if (!this.active || document.hidden) { this.pause(); return; }
      this._announcedPlayback = true;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
      document.querySelectorAll('.gallery-film-video').forEach(video => {
        if (video !== this.$refs.video) video.pause();
      });
    },
    onPause() {
      if (!this._announcedPlayback) return;
      this._announcedPlayback = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    onEnded() { this._resumeState = null; this.onPause(); },
    onError() { this.failed = true; this.onPause(); },
    pause() { if (this._resumeState) this._resumeState.playing = false; this.$refs.video?.pause(); this.onPause(); },
    syncTheme() { this.siteDark = initialExhibitionDarkMode(); },
    async restorePlayback() {
      const state = this._resumeState;
      if (!state) return;
      this._resumeState = null;
      const video = this.$refs.video;
      video.currentTime = Math.min(state.time, Math.max(0, video.duration - .05));
      if (state.playing && this.active && !document.hidden) {
        try { await video.play(); }
        catch (error) { if (error.name !== 'AbortError') this.failed = true; }
      }
    },
    otherAudio(event) { if (event.detail !== this) this.pause(); },
    visibilityChanged() { if (document.hidden) this.pause(); },
  },
};
</script>

<style scoped>
.gallery-film{--film-cover:#f5f3ef;container-type:inline-size;width:100%;min-width:0;margin:0;color:var(--case-ink,var(--ex-ink,var(--site-text-primary,#36332f)));font:14px/1.4 'Quattrocento Sans',sans-serif}
.gallery-film-frame{position:relative;aspect-ratio:16/9;overflow:hidden;border-radius:3px;background:var(--film-cover);box-shadow:0 0 0 1px color-mix(in srgb,currentColor 10%,transparent)}
.gallery-film.is-dark{--film-cover:#252221}
.gallery-film.is-unframed .gallery-film-frame{border:0;border-radius:0;box-shadow:none}
.gallery-film-video{display:block;width:100%;height:100%;object-fit:contain}
.gallery-film-play{position:absolute;inset:0;display:block;width:100%;height:100%;padding:0;border:0;background:none;color:#363a33;font:16px/1.3 'Quattrocento Sans',sans-serif;cursor:var(--site-interactive-cursor,pointer);user-select:none}
.gallery-film-poster{position:absolute;inset:0;overflow:hidden;pointer-events:none;background:var(--film-cover)}
/* Separate the model from the film frame's titles and shaded edges. */
.gallery-film-poster-model{position:absolute;inset:0;display:block;width:100%;height:100%;max-width:none;object-fit:contain;pointer-events:none;transform:var(--model-rest-transform,none);transform-origin:35% 60%;clip-path:polygon(9.27% 65.09%,16.67% 56.30%,16.61% 52.41%,11.82% 50.56%,11.88% 49.54%,26.20% 37.04%,27.66% 37.41%,33.07% 32.22%,40.68% 33.80%,40.73% 35.19%,41.93% 35.09%,43.23% 34.17%,51.88% 35.83%,51.04% 42.22%,51.93% 42.41%,51.46% 47.13%,57.03% 49.35%,55.94% 56.85%,59.53% 57.50%,58.23% 72.78%,54.69% 75.37%,52.81% 86.20%,28.54% 73.89%,27.19% 74.91%,17.24% 70%,17.19% 69.07%);transition:transform 240ms cubic-bezier(.2,.65,.3,1)}
.gallery-film-poster-heading{position:absolute;inset:7% 0 auto;display:flex;flex-direction:column;align-items:center;gap:1.3cqw}
.gallery-film-poster-title{display:flex;align-items:center;justify-content:center;gap:1.15cqw;font:400 4.25cqw/1.1 'EB Garamond',serif;letter-spacing:-.035em;white-space:nowrap}
.gallery-film-poster-title .gallery-mark{width:3cqw;height:2.72cqw}
.gallery-film-poster-project{display:flex;align-items:baseline;gap:.8cqw;font:1.2cqw/1.3 'Quattrocento Sans',sans-serif}
.gallery-film-poster-project>span{font:1.75cqw/1.3 'EB Garamond',serif}
.gallery-film-poster-url{position:absolute;bottom:4.6%;left:0;right:0;font:1.25cqw/1.2 'Quattrocento Sans',sans-serif}
.gallery-film-poster-credit{position:absolute;bottom:2.7%;left:2%;font:.7cqw/1.2 'Quattrocento Sans',sans-serif;opacity:.7}
.gallery-film-play-label{position:absolute;right:9%;top:60%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:12px;padding:12px}
.gallery-film-play-icon{display:grid;place-items:center;width:56px;height:56px;border:1px solid #363a3370;border-radius:50%;background:#f5f3edb3;transition:background-color 160ms ease,border-color 160ms ease,transform 160ms ease}
.gallery-film-play-icon svg{width:24px;height:24px;fill:currentColor;stroke:none;margin-left:3px}
.gallery-film-play small{display:block;margin-top:5px;font-size:11px;letter-spacing:.04em;color:#696c65}
.gallery-film.is-dark .gallery-film-play{color:#e7e2da}
.gallery-film.is-dark .gallery-film-play-icon{border-color:#e7e2da70;background:#252321b3}
.gallery-film.is-dark .gallery-film-play small{color:#b4b1a8}
.gallery-film-play:active .gallery-film-play-icon{transform:scale(.97)}
.gallery-film-play:focus-visible{outline:2px solid currentColor;outline-offset:-4px;border-radius:3px}
.gallery-film-footer{display:flex;justify-content:flex-end;gap:16px;min-height:26px;padding-top:9px;box-sizing:border-box;font-size:11px;color:var(--case-muted,var(--ex-muted,var(--site-text-secondary,#77756f)))}
.gallery-film-footer a{color:inherit;text-decoration:none;cursor:var(--site-interactive-cursor,pointer)}
.gallery-film-footer a:focus-visible{outline:1px solid currentColor;outline-offset:3px}
@media(hover:hover) and (pointer:fine){.gallery-film-play:hover .gallery-film-poster-model{transform:var(--model-hover-transform,scale(1.035))}.gallery-film-play:hover .gallery-film-play-icon{background:#fff;border-color:#363a33;transform:scale(1.06)}.gallery-film.is-dark .gallery-film-play:hover .gallery-film-play-icon{background:#3b3934;border-color:#e7e2da}.gallery-film-footer a:hover{text-decoration:underline;text-underline-offset:3px}}
@container(max-width:520px){
  .gallery-film-play{font-size:clamp(12px,3.6cqw,16px)}
  .gallery-film-poster-heading,.gallery-film-poster-url,.gallery-film-poster-credit{display:none}
  .gallery-film-poster-model{inset:auto;left:50%;top:50%;width:144%;height:144%;--model-rest-transform:translate(-35%,-60%);--model-hover-transform:translate(-35%,-60%) scale(1.035)}
  .gallery-film-poster::after{content:'';position:absolute;inset:0;background:rgb(245 243 237 / 30%)}
  .gallery-film.is-dark .gallery-film-poster::after{background:rgb(20 19 18 / 32%)}
  .gallery-film-play-label{inset:0;transform:none;justify-content:center;gap:clamp(6px,2cqw,10px);padding:8px}
  .gallery-film-play-icon{width:clamp(32px,11cqw,48px);height:clamp(32px,11cqw,48px);background:rgb(250 249 246 / 90%)}
  .gallery-film.is-dark .gallery-film-play-icon{background:rgb(37 35 33 / 90%)}
  .gallery-film-play-icon svg{width:20px;height:20px}
  .gallery-film-play small{font-size:clamp(10px,2.8cqw,11px);margin-top:3px}
}
@media(prefers-reduced-motion:reduce){.gallery-film-poster-model,.gallery-film-play-icon{transition:none}.gallery-film-play:hover .gallery-film-poster-model{transform:var(--model-rest-transform,none)}}
</style>
